import { useState, useEffect, useCallback, useRef } from 'react';
// store
import { useSelector } from '../../../store';
// sections
import SearchContent from './SearchContent';
// components
import Breadcrumbs from '../../../components/breadcrumbs';
import TableInfo from '../../../components/table-info';
import Pagination from '../../../components/pagination';
import Dialog from '../../../components/dialog';
// @devextreme
import DataGrid, { Column } from 'devextreme-react/data-grid';
// @moment
import moment from 'moment-timezone';
// utils
import { timezones } from '../../../utils/format';
// styles
import { ListContainer, Box, PrimaryButton } from '../../../styles';
// apis
import { getInquiry } from '../../../apis';

// ----------------------------------------------------------------------

const now = timezones(moment().utc().format(), 'YYYY-MM-DDTHH:mm:ss');
const sizes = [10, 20, 30];

// ----------------------------------------------------------------------

export default function InquiryPage() {
  const group = useSelector((state) => state.group);
  const device = useSelector((state) => state.device);

  const [ interval, setInterval ] = useState('daily');

  const [ start, setStart ] = useState(new Date(now));
  const [ end, setEnd ] = useState(new Date(now));

  const [ page, setPage ] = useState(0);
  const [ size, setSize ] = useState(20);
  
  const [ data, setData ] = useState({ content: [], totalElements: 0, totalPages: 1 });
  const { content: rows, totalElements: total, totalPages: pages } = data;

  const [ searched, setSearched ] = useState(null);
  
  const [ message, setMessage ] = useState(null);
  const handleClose = () => setMessage(null);

  useEffect(() => {
    if ( device ) handleSearch();
    else setData({ content: [], totalElements: 0, totalPages: 1 });
  }, [device]);

  const handlePage = (value: number) => {
    setPage(value-1);
    handleSearch({page: value-1, size});
  };

  const handleSize = (value: number) => {
    setPage(0);
    setSize(value);
    handleSearch({page: 0, size: value});
  };

  const handleSearch = async (paging?: { page: number, size: number }) => {

    if ( !start || !end ) return;
    let difference = Math.abs( start.getTime() - end.getTime() );
    difference = Math.ceil( difference / (1000 * 60 * 60 * 24) );

    if ( (interval === 'weekly' || interval === 'setting' ) && difference > 90 ) return setMessage('90일 이내로 검색해주세요.');

    if ( start > end ) return setMessage('검색 시작 시간은 종료 시간보다 이전이어야 합니다.\n검색 기간을 다시 한번 확인해 주세요.');

    if ( moment(end).isAfter(now, 'day') ) return;

    if ( !paging ) setPage(0);

    getInquiry(
      setData, device.iotDeviceId,
      paging ? searched.start : start,
      paging ? searched.end : (interval === 'daily' || interval === 'monthly') ? start : end,
      paging ? searched.interval : interval,
      paging ? { page: paging.page, size: paging.size } : { page: 0, size }
    )
    .then(() => setSearched({
      interval: paging ? searched.interval : interval,
      start: paging ? searched.start : start,
      end: paging ? searched.end : (interval === 'daily' || interval === 'monthly') ? start : end
    }))
    .catch(() => {
      setSearched(null);
      setData({ content: [], totalElements: 0, totalPages: 1 });
    });
  };

  const gridRef = useRef(null);

  const customizeColumns = useCallback((columns: any) => {
    const column: any = [];
    if ( rows[0]?.waterQualities?.length > 0 ) {
      for (let i = 0; i < rows[0].waterQualities.length; i++) {
        column.push({
          caption:  `${rows[0].waterQualities[i].name} (${rows[0].waterQualities[i].unit})`,
          dataField: `${rows[0].waterQualities[i].name} (${rows[0].waterQualities[i].unit})`,
          alignment: 'center',
        }, {
          caption:  `${rows[0].waterQualities[i].name} 알람`,
          dataField: `${rows[0].waterQualities[i].name}alarm`,
          alignment: 'center',
        }, {
          caption:  `${rows[0].waterQualities[i].name} 센서 상태`,
          dataField: `${rows[0].waterQualities[i].name}status`,
          alignment: 'center',
        });
      }
    }
    if ( device?.iotDeviceId ) column.push({ caption:  `${group.waterTemperature} (℃)`, dataField: 'waterTemperature', alignment: 'center' });

    if ( rows[0]?.waterQualityPredictions?.length > 0 ) {
      for (let i = 0; i < rows[0].waterQualityPredictions.length; i++) {
        column.push({
          caption:  `${rows[0].waterQualityPredictions[i].name} (${rows[0].waterQualityPredictions[i].unit})`,
          dataField: `${rows[0].waterQualityPredictions[i].name} (${rows[0].waterQualityPredictions[i].unit})`,
          alignment: 'center',
        }, {
          caption:  `${rows[0].waterQualityPredictions[i].name} 알람`,
          dataField: `${rows[0].waterQualityPredictions[i].name}alarm`,
          alignment: 'center',
        });
      }
    }

    column.push(
      { caption:  '장비 온도 (℃)', dataField: 'boardTemperature', alignment: 'center' },
      { caption:  '배터리 (%)', dataField: 'batteryLevel', alignment: 'center' },
      { caption:  '신호 감도 (dBm)', dataField: 'rsrp', alignment: 'center' });
      
    for (let i = 0; i < column.length; i++) {
      columns.push(column[i]);
    }
  }, [rows]);

  const cellRender = ({data}: any) => {
    return timezones(data.acquisitionAt, 'YYYY-MM-DD HH:mm:ss');
  };

  return (
    <ListContainer>
      <Breadcrumbs heading='데이터 조회' />

      <SearchContent
        interval={interval}
        setInterval={setInterval}
        start={start}
        setStart={setStart}
        end={end}
        setEnd={setEnd}
        searched={searched}
        handleSearch={handleSearch}
        gridRef={gridRef}
        setMessage={setMessage}
      />
      
      <Box>
        <TableInfo total={total} pages={pages} page={page} sizes={sizes} size={size} handleSize={handleSize} />

        <DataGrid
          ref={gridRef}
          dataSource={rows}
          noDataText=''
          columnAutoWidth={true}
          paging={{enabled: false}}
          customizeColumns={customizeColumns}
        >
          <Column caption='시간' dataField='acquisitionAt' alignment='center' cellRender={cellRender}  />
        </DataGrid>

        <Pagination page={page} pages={pages} size={size} handlePage={handlePage} />
      </Box>

      { message &&
        <Dialog
          handleClose={handleClose}
        >
          <p>{ message }</p>
          
          <PrimaryButton type='button' onClick={() => handleClose()} style={{'marginTop': '40px'}}>확인</PrimaryButton>
        </Dialog>
      }
    </ListContainer>
  );
}